import { projectCheckoutProviderSchema } from "schemas/agentIntegration";
import { connectedChatRoutingPolicySchema } from "schemas/connectedChat";
import { registrantContactSchema } from "schemas/domainRegistration";
import { amountSchema } from "schemas/money";
import { currencySchema } from "schemas/project";
import { projectHostingTypeSchema } from "schemas/projectHostingType";
import { userWorkspaceRoleSchema } from "schemas/routes/workspace";
import {
  displayModeSchema,
  selectModeSelectionTypeSchema,
} from "schemas/selectMode";
import { workspaceUpdatesSchema } from "schemas/workspace";
import { workspaceDefaultProjectTypeSchema } from "schemas/workspaceDefaultProjectType";
import { z } from "zod";

import { agentPermissionActionSchema } from "./agentPermissions";
import {
  orderEventActorSchema,
  orderEventMetadataSchema,
  orderItemFulfillmentStatusSchema,
  orderItemOptionValuesSnapshotSchema,
} from "./order";

// Collections are tables, fields are schema columns, records are rows.
// Body types are JSON payloads; settings inputs are scoped by the runtime token.

// NOTE (Max, 2026-05-05): CollectionValue stays a hand-written TS type because it is recursive and
// the type generator cannot emit a self-referential alias for a top-level
// ZodLazy schema. The companion `collectionValueSchema` below is referenced
// by other schemas via the externalTypeMapping for "CollectionValue".
export type CollectionValue =
  | string
  | number
  | boolean
  | null
  | CollectionValue[]
  | { [key: string]: CollectionValue };

export const collectionValueSchema: z.ZodType<CollectionValue> = z
  .lazy(() => {
    return z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.null(),
      z.array(collectionValueSchema),
      z.record(z.string(), collectionValueSchema),
    ]);
  })
  .describe("CollectionValue");

export const collectionFieldTypeSchema = z
  .enum([
    "string",
    "integer",
    "float",
    "boolean",
    "datetime",
    "json",
    "phone",
    "email",
  ])
  .describe("CollectionFieldType");

export const collectionFieldInputSchema = z
  .object({
    key: z.string(),
    displayName: z.string(),
    type: collectionFieldTypeSchema,
    nullable: z.boolean().optional(),
  })
  .describe("CollectionFieldInput");

export const createCollectionBodySchema = z
  .object({
    collectionKey: z.string(),
    displayName: z.string(),
    fields: z.array(collectionFieldInputSchema),
  })
  .describe("CreateCollectionBody");

export const updateCollectionBodySchema = z
  .object({
    displayName: z.string().optional(),
    newCollectionKey: z.string().optional(),
  })
  .describe("UpdateCollectionBody");

export const updateCollectionInputSchema = updateCollectionBodySchema
  .extend({
    collectionKey: z.string(),
  })
  .describe("UpdateCollectionInput");

export const createCollectionFieldInputSchema = z
  .object({
    collectionKey: z.string(),
    field: collectionFieldInputSchema,
  })
  .describe("CreateCollectionFieldInput");

export const updateCollectionFieldBodySchema = z
  .object({
    displayName: z.string().optional(),
    newKey: z.string().optional(),
    nullable: z.boolean().optional(),
  })
  .describe("UpdateCollectionFieldBody");

export const updateCollectionFieldInputSchema = updateCollectionFieldBodySchema
  .extend({
    collectionKey: z.string(),
    key: z.string(),
  })
  .describe("UpdateCollectionFieldInput");

export const deleteCollectionFieldInputSchema = z
  .object({
    collectionKey: z.string(),
    key: z.string(),
  })
  .describe("DeleteCollectionFieldInput");

export const schemaVariantSchema = z
  .enum(["draft", "published"])
  .describe("SchemaVariant");

export const recordWriteOptionsSchema = z
  .object({
    schemaVariant: schemaVariantSchema.optional(),
    schemaRevision: z.string().optional(),
  })
  .describe("RecordWriteOptions");

export const createRecordBodySchema = z
  .object({
    valuesByKey: z.record(z.string(), collectionValueSchema),
  })
  .describe("CreateRecordBody");

export const createRecordInputSchema = createRecordBodySchema
  .extend(recordWriteOptionsSchema.shape)
  .extend({ collectionKey: z.string() })
  .describe("CreateRecordInput");

export const updateRecordBodySchema = z
  .object({
    valuesByKey: z.record(z.string(), collectionValueSchema),
  })
  .describe("UpdateRecordBody");

export const updateRecordInputSchema = updateRecordBodySchema
  .extend(recordWriteOptionsSchema.shape)
  .extend({ collectionKey: z.string(), recordId: z.string() })
  .describe("UpdateRecordInput");

export const getRecordInputSchema = z
  .object({
    collectionKey: z.string(),
    recordId: z.string(),
  })
  .describe("GetRecordInput");

export const deleteRecordInputSchema = z
  .object({
    collectionKey: z.string(),
    recordId: z.string(),
  })
  .describe("DeleteRecordInput");

export const deleteRecordResponseSchema = z
  .object({
    recordId: z.string(),
  })
  .describe("DeleteRecordResponse");

export const queryFilterSchema = z
  .object({
    key: z.string(),
    op: z.literal("equals"),
    value: collectionValueSchema,
  })
  .describe("QueryFilter");

export const queryRecordsBodySchema = z
  .object({
    limit: z.number().optional(),
    offset: z.number().optional(),
    filter: z.array(queryFilterSchema).optional(),
  })
  .describe("QueryRecordsBody");

export const queryRecordsInputSchema = queryRecordsBodySchema
  .extend({
    collectionKey: z.string(),
  })
  .describe("QueryRecordsInput");

export const collectionFieldSchema = z
  .object({
    fieldId: z.string(),
    key: z.string(),
    displayName: z.string(),
    type: collectionFieldTypeSchema,
    nullable: z.boolean(),
  })
  .describe("CollectionField");

export const collectionMetadataSchema = z
  .object({
    collectionId: z.string(),
    collectionKey: z.string(),
    displayName: z.string(),
    fields: z.array(collectionFieldSchema),
  })
  .describe("CollectionMetadata");

export const publishCollectionInputSchema = z
  .object({
    collectionKey: z.string(),
  })
  .describe("PublishCollectionInput");

export const collectionSchemaInputSchema = z
  .object({
    collectionKey: z.string(),
    variant: schemaVariantSchema.optional(),
  })
  .describe("CollectionSchemaInput");

export const collectionSchemaSnapshotSchema = z
  .object({
    collectionKey: z.string(),
    variant: schemaVariantSchema,
    fields: z.array(collectionFieldSchema),
    jsonSchema: z.record(z.string(), z.unknown()),
    publishedAt: z.string().nullable(),
    schemaRevision: z.string().nullable(),
  })
  .describe("CollectionSchemaSnapshot");

export const publishCollectionResponseSchema = z
  .object({
    collection: collectionMetadataSchema,
    schema: collectionSchemaSnapshotSchema,
  })
  .describe("PublishCollectionResponse");

export const recordResponseSchema = z
  .object({
    recordId: z.string(),
    collectionKey: z.string(),
    valuesByKey: z.record(z.string(), collectionValueSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .describe("RecordResponse");

export const catalogResponseSchema = z
  .object({
    collections: z.array(collectionMetadataSchema),
  })
  .describe("CatalogResponse");

export const publishedSiteSchema = z
  .object({
    subdomain: z.string(),
    siteHost: z.string(),
    customDomain: z.object({ value: z.string() }).nullable(),
    faviconUrls: z.object({
      lightMode: z.string().nullable(),
      darkMode: z.string().nullable(),
    }),
  })
  .describe("PublishedSite");

export const settingsUserRoleSchema = z
  .enum([
    "superuser",
    "workspaceOwner",
    "workspaceMember",
    "projectMember",
    "none",
  ])
  .describe("SettingsUserRole");

// Bedrock orders mini-app wire schemas. These are the shapes returned by
// `apps/bedrock-api/src/routes/orders.ts` and consumed by the mini-app via
// `packages/bedrock-sdk/client.ts`.

const isoTimestampSchema = z.string();

export const bedrockOrderItemSchema = z
  .object({
    id: z.uuid(),
    variantId: z.uuid(),
    sku: z.string().nullable(),
    variantTitleSnapshot: z.string(),
    imageUrlSnapshot: z.string().nullable(),
    optionValuesSnapshot: orderItemOptionValuesSnapshotSchema,
    quantity: z.number().int(),
    unitPrice: amountSchema,
    totalPrice: amountSchema,
  })
  .describe("BedrockOrderItem");

export type BedrockOrderItem = z.infer<typeof bedrockOrderItemSchema>;

export const bedrockFulfillmentStatusSchema = z
  .enum(["pending", "open", "success", "cancelled", "error", "failure"])
  .describe("BedrockFulfillmentStatus");

export type BedrockFulfillmentStatus = z.infer<
  typeof bedrockFulfillmentStatusSchema
>;

export const bedrockFulfillmentSchema = z
  .object({
    id: z.uuid(),
    fulfillmentOrderId: z.uuid(),
    status: bedrockFulfillmentStatusSchema,
    trackingCarrier: z.string().nullable(),
    trackingNumber: z.string().nullable(),
    trackingUrl: z.string().nullable(),
    shippedAt: isoTimestampSchema.nullable(),
    deliveredAt: isoTimestampSchema.nullable(),
    lineItemQuantities: z.array(
      z.object({
        fulfillmentOrderLineItemId: z.uuid(),
        quantity: z.number().int(),
      }),
    ),
    createdAt: isoTimestampSchema,
  })
  .describe("BedrockFulfillment");

export type BedrockFulfillment = z.infer<typeof bedrockFulfillmentSchema>;

export const bedrockFulfillmentOrderStatusSchema = z
  .enum([
    "open",
    "inProgress",
    "closed",
    "cancelled",
    "onHold",
    "incomplete",
    "scheduled",
  ])
  .describe("BedrockFulfillmentOrderStatus");

export type BedrockFulfillmentOrderStatus = z.infer<
  typeof bedrockFulfillmentOrderStatusSchema
>;

export const bedrockFulfillmentOrderRequestStatusSchema = z
  .enum([
    "unsubmitted",
    "submitted",
    "accepted",
    "rejected",
    "cancellationRequested",
    "cancellationAccepted",
    "cancellationRejected",
    "closed",
  ])
  .describe("BedrockFulfillmentOrderRequestStatus");

export type BedrockFulfillmentOrderRequestStatus = z.infer<
  typeof bedrockFulfillmentOrderRequestStatusSchema
>;

export const bedrockFulfillmentOrderLineItemSchema = z
  .object({
    id: z.uuid(),
    orderItemId: z.uuid(),
    totalQuantity: z.number().int(),
    remainingQuantity: z.number().int(),
  })
  .describe("BedrockFulfillmentOrderLineItem");

export type BedrockFulfillmentOrderLineItem = z.infer<
  typeof bedrockFulfillmentOrderLineItemSchema
>;

export const bedrockFulfillmentOrderSchema = z
  .object({
    id: z.uuid(),
    orderId: z.uuid(),
    assignedLocationId: z.uuid(),
    status: bedrockFulfillmentOrderStatusSchema,
    requestStatus: bedrockFulfillmentOrderRequestStatusSchema,
    lineItems: z.array(bedrockFulfillmentOrderLineItemSchema),
    fulfillments: z.array(bedrockFulfillmentSchema),
    createdAt: isoTimestampSchema,
  })
  .describe("BedrockFulfillmentOrder");

export type BedrockFulfillmentOrder = z.infer<
  typeof bedrockFulfillmentOrderSchema
>;

export const bedrockOrderEventSchema = z
  .object({
    id: z.uuid(),
    orderId: z.uuid(),
    kind: z.string(),
    actor: orderEventActorSchema,
    metadata: orderEventMetadataSchema,
    fulfillmentOrderId: z.uuid().nullable(),
    fulfillmentId: z.uuid().nullable(),
    createdAt: isoTimestampSchema,
    editedAt: isoTimestampSchema.nullable(),
    deletedAt: isoTimestampSchema.nullable(),
  })
  .describe("BedrockOrderEvent");

export type BedrockOrderEvent = z.infer<typeof bedrockOrderEventSchema>;

export const bedrockOrderListItemSchema = z
  .object({
    id: z.uuid(),
    createdAt: isoTimestampSchema,
    customerEmail: z.string().nullable(),
    financialStatus: z.string(),
    fulfillmentStatus: orderItemFulfillmentStatusSchema,
    totalAmount: amountSchema,
    currencyCode: z.string(),
  })
  .describe("BedrockOrderListItem");

export type BedrockOrderListItem = z.infer<typeof bedrockOrderListItemSchema>;

export const bedrockOrderDetailSchema = bedrockOrderListItemSchema
  .extend({
    items: z.array(bedrockOrderItemSchema),
    fulfillmentOrders: z.array(bedrockFulfillmentOrderSchema),
    events: z.array(bedrockOrderEventSchema),
    refundedAmount: amountSchema,
    subtotalAmount: amountSchema,
    taxAmount: amountSchema,
    shippingAmount: amountSchema,
    discountAmount: amountSchema,
    stripePaymentIntentId: z.string().nullable(),
  })
  .describe("BedrockOrderDetail");

export type BedrockOrderDetail = z.infer<typeof bedrockOrderDetailSchema>;

export const orderListResponseSchema = z
  .object({
    orders: z.array(bedrockOrderDetailSchema),
    nextCursor: z.string().nullable(),
  })
  .describe("OrderListResponse");

export type OrderListResponse = z.infer<typeof orderListResponseSchema>;

export const orderOverviewResponseSchema = z
  .object({
    orderCount: z.number().int().nonnegative(),
    averageItemsPerOrder: z.number().nonnegative(),
    fulfilledOrderCount: z.number().int().nonnegative(),
    unfulfilledOrderCount: z.number().int().nonnegative(),
    partialOrderCount: z.number().int().nonnegative(),
    cancelledOrderCount: z.number().int().nonnegative(),
    refundedOrderCount: z.number().int().nonnegative(),
    // null when the range has no sold orders, or sold orders span multiple
    // currencies — a cross-currency sum has no single honest number.
    netRevenue: z
      .object({
        amount: z.number().int().nonnegative(),
        currencyCode: z.string(),
      })
      .nullable(),
  })
  .describe("OrderOverviewResponse");

export type OrderOverviewResponse = z.infer<typeof orderOverviewResponseSchema>;

export const orderDetailResponseSchema = z
  .object({
    order: bedrockOrderDetailSchema,
  })
  .describe("OrderDetailResponse");

export type OrderDetailResponse = z.infer<typeof orderDetailResponseSchema>;

export const orderEventListResponseSchema = z
  .object({
    events: z.array(bedrockOrderEventSchema),
    nextCursor: z.string().nullable(),
  })
  .describe("OrderEventListResponse");

export type OrderEventListResponse = z.infer<
  typeof orderEventListResponseSchema
>;

export const orderEventResponseSchema = z
  .object({
    event: bedrockOrderEventSchema,
  })
  .describe("OrderEventResponse");

export type OrderEventResponse = z.infer<typeof orderEventResponseSchema>;

export const orderItemResponseSchema = z
  .object({
    orderItem: bedrockOrderItemSchema,
  })
  .describe("OrderItemResponse");

export type OrderItemResponse = z.infer<typeof orderItemResponseSchema>;

export const refundResponseSchema = z
  .object({
    stripeRefundId: z.string(),
  })
  .describe("RefundResponse");

export type RefundResponse = z.infer<typeof refundResponseSchema>;

export const fulfillmentOrderResponseSchema = z
  .object({
    fulfillmentOrder: bedrockFulfillmentOrderSchema,
  })
  .describe("FulfillmentOrderResponse");

export type FulfillmentOrderResponse = z.infer<
  typeof fulfillmentOrderResponseSchema
>;

export const fulfillmentResponseSchema = z
  .object({
    fulfillment: bedrockFulfillmentSchema,
  })
  .describe("FulfillmentResponse");

export type FulfillmentResponse = z.infer<typeof fulfillmentResponseSchema>;

export const orderChangedEventSchema = z
  .object({
    type: z.literal("order.changed"),
    payload: z.object({
      orderId: z.uuid(),
      projectId: z.uuid(),
    }),
  })
  .describe("OrderChangedEvent");

export type OrderChangedEvent = z.infer<typeof orderChangedEventSchema>;

export const settingsCapabilitiesSchema = z
  .object({
    userRole: settingsUserRoleSchema,
    canUpdateProject: z.boolean(),
    canMoveProject: z.boolean(),
    canDeleteProject: z.boolean(),
    canUpdateWorkspace: z.boolean(),
    canDeleteWorkspace: z.boolean(),
    canManageMembers: z.boolean(),
    canManageProjectMembers: z.boolean(),
    canManagePublicApiTokens: z.boolean(),
    canUpdateWorkspaceMembership: z.boolean(),
    canManageAccount: z.boolean(),
    canViewReferrals: z.boolean(),
    canManageBilling: z.boolean(),
  })
  .describe("SettingsCapabilities");

export const workspaceSettingsSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    domain: z.string().nullable(),
    timeZone: z.string().nullable(),
    currency: currencySchema.nullable(),
    connectedChatRoutingPolicy: connectedChatRoutingPolicySchema.nullable(),
    defaultProjectType: workspaceDefaultProjectTypeSchema,
    paymentProcessor: z.string().nullable(),
    // Customer-declared annual revenue in USD — a signup bucket floor; null = unknown.
    annualRevenue: z.number().int().nullable(),
  })
  .describe("WorkspaceSettings");

// A site belonging to the project, used to populate the "default site" picker.
// Sites have no display name of their own, so the subdomain is the label.
export const projectSiteSchema = z
  .object({
    id: z.uuid(),
    subdomain: z.string(),
    // NOTE (Max, 2026-07-23): latest successful publish; null = never went live.
    lastPublishedAt: z.string().nullable(),
  })
  .describe("ProjectSite");

// The seller's public-facing address (email footers today; invoices/marketing
// compliance later). Structured so it stays analyzable and localizable —
// display lines are derived, never stored. Same field vocabulary as
// `orderAddressSchema`.
export const storeAddressSchema = z
  .object({
    line1: z.string().trim().min(1).max(255),
    line2: z.string().trim().max(255).optional(),
    city: z.string().trim().min(1).max(255),
    state: z.string().trim().max(255).optional(),
    postalCode: z.string().trim().min(1).max(32),
    // ISO 3166-1 alpha-2, matching orderAddressSchema.
    country: z.string().regex(/^[A-Z]{2}$/),
  })
  .describe("StoreAddress");

/** The one-line rendering for email footers and settings rows; v1 is a plain
 * join — per-country ordering can iterate because the stored shape is right.
 * Optional fields accept null so MerchantAddress rows pass without mapping. */
export function formatAddressLine(address: {
  line1: string;
  line2?: string | null;
  city: string;
  state?: string | null;
  postalCode: string;
  country: string;
}): string {
  const cityLine = [
    address.city,
    [address.state, address.postalCode].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [address.line1, address.line2, cityLine, address.country]
    .filter(Boolean)
    .join(", ");
}

export const projectSettingsSchema = z
  .object({
    id: z.uuid(),
    name: z.string().nullable(),
    hostingType: projectHostingTypeSchema,
    ownerWorkspaceId: z.uuid(),
    // The owner is implicitly subscribed to new-order notifications; the UI
    // needs to know whose toggle defaults on.
    ownerId: z.uuid().nullable(),
    ownerWorkspace: workspaceSettingsSchema.pick({
      id: true,
      name: true,
      domain: true,
    }),
    timeZone: z.string().nullable(),
    currency: currencySchema.nullable(),
    shopifyUrl: z.string().nullable(),
    // Whether a live Shopify integration is connected. The settings UI blocks
    // project deletion while one exists so it must be disconnected first,
    // otherwise the integration is orphaned on the deleted project (REPL-28372).
    hasConnectedShopifyIntegration: z.boolean(),
    checkoutProvider: projectCheckoutProviderSchema,
    // Which site the sandbox provisions + runs on :3000. Null falls back to the
    // project's sole site. `sites` is the choosable set for the settings picker.
    defaultSiteId: z.uuid().nullable(),
    sites: z.array(projectSiteSchema),
    // Reply-To for buyer emails; write-validated as an email, read tolerantly.
    supportEmail: z.string().nullable(),
    // Buyer-email footer address; null hides the footer line.
    storeAddress: storeAddressSchema.nullable(),
    // Refund-email ETA copy; null renders the platform default.
    refundEtaBusinessDays: z.string().nullable(),
  })
  .describe("ProjectSettings");

export const movableWorkspaceSchema = workspaceSettingsSchema
  .pick({ id: true, name: true })
  .describe("MovableWorkspace");

export const workspaceMembershipSchema = z
  .object({
    id: z.uuid(),
    userId: z.uuid(),
    email: z.email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    profileImageUrl: z.string().nullable(),
    registeredAt: z.string().nullable(),
    role: userWorkspaceRoleSchema.nullable(),
    projectMemberships: z.array(
      z.object({
        id: z.uuid(),
        name: z.string().nullable(),
      }),
    ),
  })
  .describe("WorkspaceMembership");

// A collaborator granted access to a single project via `project_membership`.
// Access is binary (no role), unlike workspace membership.
export const projectMemberSchema = z
  .object({
    id: z.uuid(),
    userId: z.uuid(),
    email: z.email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    profileImageUrl: z.string().nullable(),
    registeredAt: z.string().nullable(),
  })
  .describe("ProjectMember");

// Batch invites partially succeed: emails we can't invite are skipped and
// reported in `failedEmails` while everyone else is added.
export const projectMembershipInviteResultSchema = z
  .object({
    members: z.array(projectMemberSchema),
    failedEmails: z.array(z.email()),
  })
  .describe("ProjectMembershipInviteResult");

export const workspaceMembershipInviteResultSchema = z
  .object({
    members: z.array(workspaceMembershipSchema),
    failedEmails: z.array(z.email()),
  })
  .describe("WorkspaceMembershipInviteResult");

// Team Usage settings page rollup — counts and timestamps only, never prompt text.
export const teamUsageMemberSchema = z
  .object({
    userId: z.uuid(),
    email: z.email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    profileImageUrl: z.string().nullable(),
    prompts: z.number(),
    sessions: z.number(),
    publishes: z.number(),
    creditsCents: z.number(),
    lastActiveAt: z.string().nullable(),
    // Per-day series, each aligned index-for-index with `TeamUsage.days`.
    dailyPrompts: z.array(z.number()),
    dailySessions: z.array(z.number()),
    dailyPublishes: z.array(z.number()),
    dailyCreditsCents: z.array(z.number()),
  })
  .describe("TeamUsageMember");

export const teamUsageSchema = z
  .object({
    windowDays: z.number(),
    // UTC day keys (YYYY-MM-DD) covering the window, oldest first.
    days: z.array(z.string()),
    members: z.array(teamUsageMemberSchema),
  })
  .describe("TeamUsage");

export const accountSettingsSchema = z
  .object({
    id: z.uuid(),
    email: z.email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    name: z.string(),
    createdAt: z.string(),
    verifiedAt: z.string().nullable(),
    profileImageUrl: z.string().nullable(),
    isSuperuser: z.boolean(),
  })
  .describe("AccountSettings");

export const referralsSnapshotSchema = z
  .object({
    publicToken: z.string().nullable(),
  })
  .describe("ReferralsSnapshot");

export const settingsAgentPermissionToolSchema = z
  .object({
    target: z.string().min(1),
    label: z.string().min(1),
    action: agentPermissionActionSchema,
  })
  .describe("SettingsAgentPermissionTool");

export const settingsAgentPermissionGroupIconSchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal("reploSymbol") }),
    z.object({ type: z.literal("logo"), url: z.string().min(1).nullable() }),
  ])
  .describe("SettingsAgentPermissionGroupIcon");

export const settingsAgentPermissionGroupSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    icon: settingsAgentPermissionGroupIconSchema,
    tools: z.array(settingsAgentPermissionToolSchema),
  })
  .describe("SettingsAgentPermissionGroup");

export const settingsAgentPermissionsSchema = z
  .object({
    groups: z.array(settingsAgentPermissionGroupSchema),
  })
  .describe("SettingsAgentPermissions");

// One switch per scope: enabling turns on recall, capture, and dreaming together (default on).
const memoryScopeToggleSchema = z.object({
  user: z.boolean().default(true),
  project: z.boolean().default(true),
});

export const agentMemoryConfigSchema = z
  .object({
    enabled: memoryScopeToggleSchema.default({ user: true, project: true }),
  })
  .describe("AgentMemoryConfig");

// Partial PATCH: each scope toggle is independently omittable so the server can merge without resetting the untouched scope.
export const updateAgentMemoryConfigInputSchema = z
  .object({
    enabled: memoryScopeToggleSchema.partial().optional(),
  })
  .describe("UpdateAgentMemoryConfigInput");

// Single source of truth for the default: memory (recall + capture + dreaming) is on unless a user explicitly turned a scope off.
export function getDefaultAgentMemoryConfig() {
  return agentMemoryConfigSchema.parse({});
}

// Storage is split by ownership: the user scope is global on the user row (memory_enabled_at); the project scope is on the project row.
export type AgentUserMemoryConfig = { enabled: boolean };
export type AgentProjectMemoryConfig = { enabled: boolean };

// Recombine the two storage scopes into the API shape; a missing row means the user never changed it, so it falls back to the on-by-default value.
// The `user`/`project` storage owners map onto the product-facing `enabled.user`/`enabled.project` toggles.
export function composeAgentMemoryConfig({
  user,
  project,
}: {
  user: AgentUserMemoryConfig | null;
  project: AgentProjectMemoryConfig | null;
}) {
  const defaults = getDefaultAgentMemoryConfig();
  return {
    enabled: {
      user: user?.enabled ?? defaults.enabled.user,
      project: project?.enabled ?? defaults.enabled.project,
    },
  };
}

export const workspaceProjectSchema = z
  .object({
    id: z.uuid(),
    name: z.string().nullable(),
    hostingType: projectHostingTypeSchema,
  })
  .describe("WorkspaceProject");

export const settingsBootstrapSchema = z
  .object({
    project: projectSettingsSchema,
    workspace: workspaceSettingsSchema,
    workspaceMembership: z.array(workspaceMembershipSchema),
    projectMembership: z.array(projectMemberSchema),
    account: accountSettingsSchema,
    referrals: referralsSnapshotSchema,
    capabilities: settingsCapabilitiesSchema,
    movableWorkspaces: z.array(movableWorkspaceSchema),
    agentPermissions: settingsAgentPermissionsSchema,
    agentMemory: agentMemoryConfigSchema,
    onboardingFinished: z.boolean(),
  })
  .describe("SettingsBootstrap");

export const workspaceProjectsResponseSchema = z
  .object({
    data: z.array(workspaceProjectSchema),
  })
  .describe("WorkspaceProjectsResponse");

export const updateProjectSettingsInputSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    timeZone: z.string().min(1).optional(),
    currency: currencySchema.optional(),
    defaultSiteId: z.uuid().optional(),
    supportEmail: z.email().nullish(),
    storeAddress: storeAddressSchema.nullish(),
    // A count or en-dash range of business days ("5" or "5–10"): refund
    // emails render it verbatim and the settings UI parses it back apart.
    refundEtaBusinessDays: z
      .string()
      .trim()
      .regex(/^[1-9]\d{0,2}(–[1-9]\d{0,2})?$/)
      .nullish(),
  })
  .describe("UpdateProjectSettingsInput");

export const setCheckoutProviderInputSchema = z
  .object({
    provider: projectCheckoutProviderSchema,
  })
  .describe("SetCheckoutProviderInput");

export const transferProjectInputSchema = z
  .object({
    workspaceId: z.uuid(),
  })
  .describe("TransferProjectInput");

export const updateAgentPermissionInputSchema = z
  .object({
    target: z.string().min(1),
    action: agentPermissionActionSchema,
  })
  .describe("UpdateAgentPermissionInput");

export const updateAgentPermissionsBatchInputSchema = z
  .object({
    targets: z.array(z.string().min(1)).min(1),
    action: agentPermissionActionSchema,
  })
  .describe("UpdateAgentPermissionsBatchInput");

export const updateWorkspaceInputSchema = workspaceUpdatesSchema.describe(
  "UpdateWorkspaceInput",
);

export const updateWorkspaceRegistrantInputSchema = z
  .object({
    contact: registrantContactSchema,
  })
  .describe("UpdateWorkspaceRegistrantInput");

export const workspaceRegistrantResponseSchema = z
  .object({
    registrant: registrantContactSchema.nullable(),
  })
  .describe("WorkspaceRegistrantResponse");

export const createWorkspaceMembershipInputSchema = z
  .object({
    emails: z.array(z.email()).min(1).max(20),
    role: userWorkspaceRoleSchema,
  })
  .describe("CreateWorkspaceMembershipInput");

export const updateWorkspaceMembershipInputSchema = z
  .object({
    userId: z.uuid(),
    role: userWorkspaceRoleSchema,
  })
  .describe("UpdateWorkspaceMembershipInput");

export const workspaceMembershipUserIdInputSchema = z
  .object({
    userId: z.uuid(),
  })
  .describe("WorkspaceMembershipUserIdInput");

export const createProjectMembershipInputSchema = z
  .object({
    emails: z.array(z.email()).min(1).max(20),
  })
  .describe("CreateProjectMembershipInput");

export const projectMembershipUserIdInputSchema = z
  .object({
    userId: z.uuid(),
  })
  .describe("ProjectMembershipUserIdInput");

export const updateAccountInputSchema = z
  .object({
    firstName: z.string().trim().min(1),
    lastName: z.string(),
  })
  .describe("UpdateAccountInput");

export const updateAccountPasswordInputSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(1),
  })
  .describe("UpdateAccountPasswordInput");

export const connectGoogleAccountInputSchema = z
  .object({
    clientId: z.string(),
    credential: z.string(),
  })
  .describe("ConnectGoogleAccountInput");

export const updateAccountAvatarInputSchema = z
  .object({
    imageBase64: z.string().max(4 * 1024 * 1024),
  })
  .describe("UpdateAccountAvatarInput");

// ---------------------------------------------------------------------------
// Shared types referenced by the miniapp <-> agent-ui RPC protocol. The RPC
// envelope, method registry, and developer-facing remote/handler types live
// in `./miniappRpc.ts`.
// ---------------------------------------------------------------------------

export const agentUiViewportSchema = z
  .enum(["responsive", "desktop", "tablet", "mobile"])
  .describe("AgentUiViewport");

// Responsive mode renders at panel width — below the lg (1024px) breakpoint on most laptops, hiding desktop styles — so every surface defaults to the scaled desktop viewport. Miniapp fallbacks are live pre-handshake code and must agree with the host default.
export const DEFAULT_AGENT_UI_VIEWPORT: z.infer<typeof agentUiViewportSchema> =
  "desktop";

export const bedrockCurrentUserSchema = z
  .object({
    name: z.string().nullable(),
    email: z.string().nullable(),
  })
  .describe("BedrockCurrentUser");

// The sound played when the agent finishes a chat. Selectable in Settings →
// Notifications and consumed by the editor's completion notification.
export const completionNotificationSoundSchema = z
  .enum([
    "chime",
    "bloom",
    "pop",
    "marimba",
    "arc",
    "coin",
    "sparkle",
    "boop",
    "zap",
    "drip",
    "glint",
    "ascend",
    "beacon",
    "bell",
    "aurora",
    "halo",
  ])
  .describe("CompletionNotificationSound");

export const notificationSoundSettingsSchema = z
  .object({
    enabled: z.boolean(),
    completionSound: completionNotificationSoundSchema,
  })
  .describe("NotificationSoundSettings");

export const agentUiStateSchema = z
  .object({
    viewport: agentUiViewportSchema,
    isDebugMode: z.boolean(),
    isSuperuser: z.boolean(),
    projectId: z.uuid().nullable(),
    userId: z.string().nullable(),
    // NOTE (Max, 2026-08-01): Display name + email, so miniapps render "by
    // {real name}" instead of "You". Absent from a pre-2026-08 parent, so
    // nullish keeps a deploy-window snapshot parseable.
    user: bedrockCurrentUserSchema.nullish(),
    workspaceId: z.uuid().nullable(),
    analyticsProjectIdOverride: z.uuid().nullish(),
    displayMode: displayModeSchema,
    // Mirrored from the agent-ui select-mode store. Miniapps that own a
    // nested sandbox iframe (e.g. website-builder's canopy preview) watch
    // this so they can declaratively tell canopy "the parent thinks the
    // current selection is now <type>" — in particular, transitioning to
    // `none` is what hides the in-iframe selection frame when the user
    // dismisses the agent-ui edit panel.
    selectionType: selectModeSelectionTypeSchema,
    // NOTE (Max, 2026-08-01): Chat-header bell preference; the Settings
    // miniapp writes it back over its own miniapp→parent notification.
    // Nullish for the same deploy-window reason as `user`.
    notificationSoundSettings: notificationSoundSettingsSchema.nullish(),
    // Parent shell's open chat, mirrored from its `?sessionId=` (a miniapp iframe can't read that itself).
    // Nullish for the same deploy-window reason as `user`.
    currentSessionId: z.string().nullish(),
  })
  .describe("AgentUiState");
